import { freezeObject, isFunction, isObject } from '@/global/utils/utils'
import { computed, ref } from 'vue'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions.js'
import ExecRoutesBridge from '../components/ExecRoutesBridge.vue'
import ItemExecutionMapPanel from '../components/ItemExecutionMapPanel.vue'
import { itemExecutionScreenView } from './useScreenView'
import { setSelectedExecRoutes, routesCount, setRoutesCount, autoSelectSingleRoute } from './useSelectedExecRoutes'

const { buildConfig } = useTableService()
const TRANSLATION_NAMESPACE = 'road-maintenance/temporary_regulation/item_execution'
const UI_VIEW = 'temporary_regulations_item_execution'
const TABLE_KEY = 'item_execution'

// Amber highlight for a count the row itself flags as not fully done
// (`has_incomplete_locations` / `has_incomplete_activities`) - same warning
// palette as the "offer required" note on the requests screen.
const INCOMPLETE_CELL_STYLE = freezeObject({
  backgroundColor: '#FFFBEB',
  color: '#92400E',
  fontWeight: '600'
})

// Only WORK_ORDER-typed statuses apply to exec-routes — same type used by the
// backend's own INIT check on delete (ExecRouteController::destroy).
const statusItems = ref([])
let statusItemsRequested = false

function fetchStatusItems () {
  if (statusItemsRequested) {
    return
  }
  statusItemsRequested = true

  api('road-maintenance').get('statuses', { types: ['WORK_ORDER'] })
    .then(({ data }) => {
      statusItems.value = data?.work_order || []
    })
    .catch((exception) => {
      console.error(exception)
      statusItemsRequested = false
    })
}

export function useTable (options) {
  const {
    trans,
    router,
    openInfoDialog,
    closeInfoDialog,
    setInfoDialogLoaderFn
  } = options

  fetchStatusItems()

  const apiConfig = freezeObject({
    get: {
      module: 'road-maintenance',
      route: 'work-orders/exec-routes'
    }
  })

  const deleteExecRouteHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      setInfoDialogLoaderFn(true)
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`work-orders/exec-routes/${id}`)
      closeInfoDialog()
      const { fetchData } = registeredTableHandlers?.[TABLE_KEY] || {}

      if (isFunction(fetchData)) {
        fetchData()
      }
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onDeleteExecRoute = (event) => {
    if (!isFunction(openInfoDialog)) {
      return
    }

    openInfoDialog({
      title: trans(`${TRANSLATION_NAMESPACE}.delete_item_execution`) + (event?.data?.description ? ' - ' + event.data.description : ''),
      message: trans(`${TRANSLATION_NAMESPACE}.delete_item_execution_message`),
      onConfirm: () => deleteExecRouteHandler(event)
    })
  }

  const onShowDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: 'TemporaryRegulationsItemExecutionDetails',
      params: {
        id: String(id)
      }
    })
  }

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowDetails),
    deleteAction(trans, onDeleteExecRoute)
  ]))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance.temporary_regulation')
      },
      {
        text: trans(`${TRANSLATION_NAMESPACE}.item_execution`),
        bolded: true
      }
    ]
  }))

  const cellsConfig = computed(() => ({
    status: {
      type: 'status',
      custom_display_value: (data) => {
        if (!data?.status || !isObject(data.status)) {
          return
        }

        return {
          ...data.status,
          description: trans(data?.status?.description || '')
        }
      },
      displayValue: 'description',
      colorObjectKey: 'color',
      textColorKey: 'text',
      backgroundColorKey: 'background'
    },
    // `active_location` is an object ({ id, name, address }) or null — the
    // currently in-progress location on the route, per the backend.
    active_location: {
      custom_display_value: (data) => data?.active_location?.name || data?.active_location?.address || '-'
    },
    // `header.style` only centers the header label itself — the cell's own
    // value is centered via `wrapperStyle`, same mechanism the `status` cell
    // above uses.
    completed_locations: {
      wrapperStyle: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      },
      style: (rowData) => rowData?.has_incomplete_locations ? INCOMPLETE_CELL_STYLE : {}
    },
    completed_activities: {
      wrapperStyle: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      },
      style: (rowData) => rowData?.has_incomplete_activities ? INCOMPLETE_CELL_STYLE : {}
    }
  }))

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/settings.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans(`${TRANSLATION_NAMESPACE}.crew_name`),
      key: 'crew_name',
      always_visible: true
    },
    {
      label: trans(`${TRANSLATION_NAMESPACE}.vehicle_registration`),
      key: 'vehicle_registration',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/work_order_details.planned_start'),
      key: 'planned_start',
      initial_value: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/work_order_details.planned_end'),
      key: 'planned_end',
      initial_value: true
    },
    {
      label: trans(`${TRANSLATION_NAMESPACE}.answer_person`),
      key: 'responsible_person',
      initial_value: true
    },
    {
      label: trans(`${TRANSLATION_NAMESPACE}.items_completed`),
      key: 'completed_locations',
      style: {
        textAlign: 'center'
      },
      initial_value: true
    },
    {
      label: trans(`${TRANSLATION_NAMESPACE}.completed_tasks`),
      key: 'completed_activities',
      style: {
        textAlign: 'center'
      },
      initial_value: true
    },
    {
      label: trans(`${TRANSLATION_NAMESPACE}.active_item`),
      key: 'active_location',
      initial_value: true
    },
    {
      label: trans('road-maintenance/excavation-service.status'),
      key: 'status',
      initial_value: true
    }
  ]))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'description',
        label: trans(`${TRANSLATION_NAMESPACE}.active_item`)
      },
      {
        key: 'from',
        label: trans(`${TRANSLATION_NAMESPACE}.date_from`),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month'
      },
      {
        key: 'to',
        label: trans(`${TRANSLATION_NAMESPACE}.date_to`),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month'
      },
      {
        key: 'status_id',
        label: trans('road-maintenance/excavation-service.status'),
        type: 'autocomplete',
        items: statusItems.value,
        autocomplete_options: {
          custom_display_value: (item) => trans(item?.description || ''),
          allowSearch: false
        }
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: TABLE_KEY,
      uiView: UI_VIEW,
      apiConfig,
      rowDblClick: onShowDetails,
      // A click on a row shows it on the map beside the table
      rowClickIndicator: 'map',
      search_and_buttons_config: {
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        showNewBtn: false
      },
      // `order: -1` moves this before the grid-switcher/column-visibility
      // icons that DataTable.vue always renders ahead of this slot — those
      // buttons don't carry an order of their own, so any negative value
      // pulls this ahead of them in the flex row.
      filters_row_end_components: [
        { component: itemExecutionScreenView.toggle, order: -1 },
        // No UI of its own - it only carries the checked routes over to the map.
        { component: ExecRoutesBridge }
      ],
      // Only one route matching the current filters - nothing to pick
      // between, so autoSelectSingleRoute (below) shows it without a
      // checkbox instead of leaving the map empty.
      onDataReceivedWithParams: (event) => {
        const rows = event?.data?.data
        const total = event?.data?.pagination?.total ?? (Array.isArray(rows) ? rows.length : 0)
        setRoutesCount(total)
        autoSelectSingleRoute(rows, total)
      },
      rowsSelection: {
        showCheckboxes: routesCount.value > 1,
        multiple: true,
        rememberSelectionByDataProperty: 'id',
        selectionsChange: ({ data }) => setSelectedExecRoutes(data)
      },
      // Gone only in the map-only view, where the map is the whole screen.
      hideTable: !itemExecutionScreenView.isTableVisible.value,
      sideTableComponent: {
        component: ItemExecutionMapPanel,
        visible: itemExecutionScreenView.isPanelVisible.value
      },
      globalStyle: itemExecutionScreenView.globalStyle.value,
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config
  }
}
